import { useState, useEffect, useRef } from "react";
import { useAuth } from "../../context/AuthContext";
import { useNavigate } from "react-router-dom";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { toast } from "sonner";
import { motion, AnimatePresence } from "framer-motion";
import axios from "axios";
import { API_URL, API_BASE_URL } from "../../config/api";
import {
  StickyNote,
  Plus,
  X,
  Check,
  Trash2,
  Bell,
  BellOff,
  Archive,
  ChevronLeft,
  ChevronRight,
  Clock,
  Calendar,
  Loader2,
  MoreVertical,
  Edit2,
  FolderOpen,
  ExternalLink,
} from "lucide-react";


const NOTE_COLORS = [
  { name: "Yellow", bg: "bg-yellow-100", border: "border-yellow-300", text: "text-yellow-900" },
  { name: "Blue", bg: "bg-blue-100", border: "border-blue-300", text: "text-blue-900" },
  { name: "Green", bg: "bg-green-100", border: "border-green-300", text: "text-green-900" },
  { name: "Pink", bg: "bg-pink-100", border: "border-pink-300", text: "text-pink-900" },
  { name: "Purple", bg: "bg-purple-100", border: "border-purple-300", text: "text-purple-900" },
  { name: "Orange", bg: "bg-orange-100", border: "border-orange-300", text: "text-orange-900" },
];

// Map legacy string colors (e.g. "yellow" from WhatsApp-created notes) to their
// numeric index. Anything invalid falls back to 0 (Yellow) so the Edit modal
// can never crash with `NOTE_COLORS[undefined].bg`.
const COLOR_NAME_TO_IDX = {
  yellow: 0, blue: 1, green: 2, pink: 3, purple: 4, orange: 5,
};
function safeColorIdx(c) {
  if (typeof c === "number" && c >= 0 && c < NOTE_COLORS.length) return c;
  if (typeof c === "string" && COLOR_NAME_TO_IDX[c.toLowerCase()] !== undefined) {
    return COLOR_NAME_TO_IDX[c.toLowerCase()];
  }
  return 0;
}

export default function StickyNotesSidebar() {
  const { token, user } = useAuth();
  const navigate = useNavigate();
  const [isOpen, setIsOpen] = useState(false);
  const [notes, setNotes] = useState([]);
  const [archivedNotes, setArchivedNotes] = useState([]);
  const [showArchive, setShowArchive] = useState(false);
  const [loading, setLoading] = useState(false);
  const [showAddNote, setShowAddNote] = useState(false);
  const [editingNote, setEditingNote] = useState(null);
  const [newNote, setNewNote] = useState({
    content: "",
    color: 0,
    reminder: null,
    pf_number: "",
  });
  const textareaRef = useRef(null);

  useEffect(() => {
    if (token) {
      fetchNotes();
    }
  }, [token]);

  // Check for due reminders
  useEffect(() => {
    const checkReminders = () => {
      const now = new Date();
      notes.forEach((note) => {
        if (note.reminder && !note.reminder_notified) {
          const reminderDate = new Date(note.reminder);
          if (reminderDate <= now) {
            toast.info(`Reminder: ${note.content.substring(0, 50)}...`, {
              duration: 10000,
              action: {
                label: "View",
                onClick: () => setIsOpen(true),
              },
            });
            // Mark as notified
            markReminderNotified(note.id);
          }
        }
      });
    };

    const interval = setInterval(checkReminders, 60000); // Check every minute
    checkReminders(); // Check immediately
    return () => clearInterval(interval);
  }, [notes]);

  const fetchNotes = async () => {
    try {
      const response = await axios.get(`${API_URL}/notes`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setNotes(response.data.notes?.filter((n) => !n.completed) || []);
      setArchivedNotes(response.data.notes?.filter((n) => n.completed) || []);
    } catch (error) {
      console.error("Error fetching notes:", error);
    }
  };

  const markReminderNotified = async (noteId) => {
    try {
      await axios.patch(
        `${API_URL}/notes/${noteId}/reminder-notified`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setNotes((prev) =>
        prev.map((n) => (n.id === noteId ? { ...n, reminder_notified: true } : n))
      );
    } catch (error) {
      console.error("Error marking reminder:", error);
    }
  };

  const handleAddNote = async () => {
    if (!newNote.content.trim()) {
      toast.error("Note content is required");
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(
        `${API_URL}/notes`,
        {
          content: newNote.content,
          color: newNote.color,
          reminder: newNote.reminder,
          pf_number: newNote.pf_number || null,
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setNotes((prev) => [response.data, ...prev]);
      setNewNote({ content: "", color: 0, reminder: null, pf_number: "" });
      setShowAddNote(false);
      toast.success("Note added");
    } catch (error) {
      toast.error("Failed to add note");
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateNote = async () => {
    if (!editingNote) return;

    setLoading(true);
    try {
      await axios.put(
        `${API_URL}/notes/${editingNote.id}`,
        {
          content: editingNote.content,
          color: editingNote.color,
          reminder: editingNote.reminder,
          pf_number: editingNote.pf_number || null,
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setNotes((prev) =>
        prev.map((n) => (n.id === editingNote.id ? editingNote : n))
      );
      setEditingNote(null);
      toast.success("Note updated");
    } catch (error) {
      toast.error("Failed to update note");
    } finally {
      setLoading(false);
    }
  };

  // Navigate to PF
  const goToPF = (pfNumber) => {
    setIsOpen(false);
    navigate(`/position-folders?search=${encodeURIComponent(pfNumber)}`);
  };

  const handleComplete = async (noteId) => {
    try {
      await axios.patch(
        `${API_URL}/notes/${noteId}/complete`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const note = notes.find((n) => n.id === noteId);
      setNotes((prev) => prev.filter((n) => n.id !== noteId));
      setArchivedNotes((prev) => [{ ...note, completed: true }, ...prev]);
      toast.success("Note completed & archived");
    } catch (error) {
      toast.error("Failed to complete note");
    }
  };

  const handleRestore = async (noteId) => {
    try {
      await axios.patch(
        `${API_URL}/notes/${noteId}/restore`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const note = archivedNotes.find((n) => n.id === noteId);
      setArchivedNotes((prev) => prev.filter((n) => n.id !== noteId));
      setNotes((prev) => [{ ...note, completed: false }, ...prev]);
      toast.success("Note restored");
    } catch (error) {
      toast.error("Failed to restore note");
    }
  };

  const handleDelete = async (noteId, isArchived = false) => {
    try {
      await axios.delete(`${API_URL}/notes/${noteId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (isArchived) {
        setArchivedNotes((prev) => prev.filter((n) => n.id !== noteId));
      } else {
        setNotes((prev) => prev.filter((n) => n.id !== noteId));
      }
      toast.success("Note deleted");
    } catch (error) {
      toast.error("Failed to delete note");
    }
  };

  const formatReminder = (reminder) => {
    if (!reminder) return null;
    const date = new Date(reminder);
    const now = new Date();
    const isOverdue = date < now;
    
    return {
      text: date.toLocaleDateString("en-GB", {
        day: "numeric",
        month: "short",
        hour: "2-digit",
        minute: "2-digit",
      }),
      isOverdue,
    };
  };

  if (!user) return null;

  return (
    <>
      {/* Toggle Button */}
      <motion.button
        className={`fixed right-0 top-1/2 -translate-y-1/2 z-40 bg-yellow-400 hover:bg-yellow-500 text-yellow-900 p-2 rounded-l-lg shadow-lg transition-colors ${
          isOpen ? "opacity-0 pointer-events-none" : "opacity-100"
        }`}
        onClick={() => setIsOpen(true)}
        whileHover={{ x: -4 }}
        data-testid="sticky-notes-toggle"
      >
        <StickyNote className="h-5 w-5" />
        {notes.length > 0 && (
          <span className="absolute -top-1 -left-1 h-5 w-5 bg-red-500 text-white text-xs rounded-full flex items-center justify-center">
            {notes.length}
          </span>
        )}
      </motion.button>

      {/* Sidebar */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ x: 320 }}
            animate={{ x: 0 }}
            exit={{ x: 320 }}
            transition={{ type: "spring", damping: 25, stiffness: 300 }}
            className="fixed right-0 top-0 h-full w-80 bg-slate-50 shadow-2xl z-50 flex flex-col"
          >
            {/* Header */}
            <div className="bg-yellow-400 p-4 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <StickyNote className="h-5 w-5 text-yellow-900" />
                <h2 className="font-bold text-yellow-900">My Notes</h2>
              </div>
              <div className="flex items-center gap-1">
                <button
                  onClick={() => setShowArchive(!showArchive)}
                  className={`p-2 rounded-lg transition-colors ${
                    showArchive
                      ? "bg-yellow-500 text-yellow-900"
                      : "hover:bg-yellow-500/50 text-yellow-900"
                  }`}
                  title={showArchive ? "Show active" : "Show archive"}
                >
                  <Archive className="h-4 w-4" />
                </button>
                <button
                  onClick={() => setIsOpen(false)}
                  className="p-2 hover:bg-yellow-500/50 rounded-lg text-yellow-900"
                >
                  <ChevronRight className="h-4 w-4" />
                </button>
              </div>
            </div>

            {/* Content */}
            <div className="flex-1 overflow-y-auto p-3 space-y-3">
              {showArchive ? (
                // Archived Notes
                <>
                  <div className="text-sm font-medium text-slate-500 flex items-center gap-2">
                    <Archive className="h-4 w-4" />
                    Archived ({archivedNotes.length})
                  </div>
                  {archivedNotes.length === 0 ? (
                    <p className="text-sm text-slate-400 text-center py-4">
                      No archived notes
                    </p>
                  ) : (
                    archivedNotes.map((note) => (
                      <NoteCard
                        key={note.id}
                        note={note}
                        onRestore={() => handleRestore(note.id)}
                        onDelete={() => handleDelete(note.id, true)}
                        isArchived
                      />
                    ))
                  )}
                </>
              ) : (
                // Active Notes
                <>
                  {/* Add New Note */}
                  {showAddNote ? (
                    <motion.div
                      initial={{ opacity: 0, y: -10 }}
                      animate={{ opacity: 1, y: 0 }}
                      className={`${NOTE_COLORS[newNote.color].bg} ${NOTE_COLORS[newNote.color].border} border-2 rounded-lg p-3 shadow-md`}
                    >
                      <textarea
                        ref={textareaRef}
                        value={newNote.content}
                        onChange={(e) =>
                          setNewNote({ ...newNote, content: e.target.value })
                        }
                        placeholder="Write your note..."
                        className="w-full bg-transparent resize-none outline-none text-sm min-h-[80px]"
                        autoFocus
                      />
                      
                      {/* Color Picker */}
                      <div className="flex items-center gap-1 mt-2 mb-2">
                        {NOTE_COLORS.map((color, idx) => (
                          <button
                            key={idx}
                            onClick={() => setNewNote({ ...newNote, color: idx })}
                            className={`h-5 w-5 rounded-full ${color.bg} ${color.border} border-2 ${
                              newNote.color === idx ? "ring-2 ring-slate-400" : ""
                            }`}
                          />
                        ))}
                      </div>

                      {/* PF Number */}
                      <div className="flex items-center gap-2 mb-2">
                        <FolderOpen className="h-4 w-4 text-slate-500" />
                        <input
                          type="text"
                          value={newNote.pf_number || ""}
                          onChange={(e) =>
                            setNewNote({ ...newNote, pf_number: e.target.value })
                          }
                          placeholder="PF number (optional)"
                          className="flex-1 text-xs p-1 rounded border bg-white/50"
                        />
                      </div>

                      {/* Reminder */}
                      <div className="flex items-center gap-2 mb-3">
                        <Bell className="h-4 w-4 text-slate-500" />
                        <input
                          type="datetime-local"
                          value={newNote.reminder || ""}
                          onChange={(e) =>
                            setNewNote({ ...newNote, reminder: e.target.value || null })
                          }
                          className="flex-1 text-xs p-1 rounded border bg-white/50"
                        />
                      </div>

                      <div className="flex justify-end gap-2">
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => {
                            setShowAddNote(false);
                            setNewNote({ content: "", color: 0, reminder: null, pf_number: "" });
                          }}
                        >
                          Cancel
                        </Button>
                        <Button size="sm" onClick={handleAddNote} disabled={loading}>
                          {loading ? (
                            <Loader2 className="h-4 w-4 animate-spin" />
                          ) : (
                            "Add"
                          )}
                        </Button>
                      </div>
                    </motion.div>
                  ) : (
                    <button
                      onClick={() => setShowAddNote(true)}
                      className="w-full p-3 border-2 border-dashed border-slate-300 rounded-lg text-slate-500 hover:border-yellow-400 hover:text-yellow-600 transition-colors flex items-center justify-center gap-2"
                    >
                      <Plus className="h-4 w-4" />
                      Add Note
                    </button>
                  )}

                  {/* Notes List */}
                  {notes.length === 0 && !showAddNote ? (
                    <p className="text-sm text-slate-400 text-center py-4">
                      No active notes. Add one!
                    </p>
                  ) : (
                    notes.map((note) => (
                      <NoteCard
                        key={note.id}
                        note={note}
                        onComplete={() => handleComplete(note.id)}
                        onDelete={() => handleDelete(note.id)}
                        onEdit={() => setEditingNote(note)}
                        onGoToPF={goToPF}
                        formatReminder={formatReminder}
                      />
                    ))
                  )}
                </>
              )}
            </div>

            {/* Edit Modal */}
            <AnimatePresence>
              {editingNote && (
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  className="absolute inset-0 bg-black/50 flex items-center justify-center p-4"
                  onClick={() => setEditingNote(null)}
                >
                  <motion.div
                    initial={{ scale: 0.9 }}
                    animate={{ scale: 1 }}
                    exit={{ scale: 0.9 }}
                    className={`${NOTE_COLORS[safeColorIdx(editingNote.color)].bg} rounded-lg p-4 w-full max-w-sm shadow-xl`}
                    onClick={(e) => e.stopPropagation()}
                  >
                    <h3 className="font-semibold mb-2">Edit Note</h3>
                    <textarea
                      value={editingNote.content}
                      onChange={(e) =>
                        setEditingNote({ ...editingNote, content: e.target.value })
                      }
                      className="w-full bg-white/50 rounded p-2 resize-none outline-none text-sm min-h-[100px]"
                    />
                    
                    <div className="flex items-center gap-1 mt-2 mb-2">
                      {NOTE_COLORS.map((color, idx) => (
                        <button
                          key={idx}
                          onClick={() => setEditingNote({ ...editingNote, color: idx })}
                          className={`h-5 w-5 rounded-full ${color.bg} ${color.border} border-2 ${
                            safeColorIdx(editingNote.color) === idx ? "ring-2 ring-slate-400" : ""
                          }`}
                        />
                      ))}
                    </div>

                    {/* PF Number */}
                    <div className="flex items-center gap-2 mb-2">
                      <FolderOpen className="h-4 w-4 text-slate-500" />
                      <input
                        type="text"
                        value={editingNote.pf_number || ""}
                        onChange={(e) =>
                          setEditingNote({ ...editingNote, pf_number: e.target.value })
                        }
                        placeholder="PF number (optional)"
                        className="flex-1 text-xs p-1 rounded border"
                      />
                    </div>

                    <div className="flex items-center gap-2 mb-3">
                      <Bell className="h-4 w-4 text-slate-500" />
                      <input
                        type="datetime-local"
                        value={editingNote.reminder || ""}
                        onChange={(e) =>
                          setEditingNote({ ...editingNote, reminder: e.target.value || null })
                        }
                        className="flex-1 text-xs p-1 rounded border"
                      />
                    </div>

                    <div className="flex justify-end gap-2">
                      <Button size="sm" variant="ghost" onClick={() => setEditingNote(null)}>
                        Cancel
                      </Button>
                      <Button size="sm" onClick={handleUpdateNote} disabled={loading}>
                        {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save"}
                      </Button>
                    </div>
                  </motion.div>
                </motion.div>
              )}
            </AnimatePresence>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}

// Note Card Component
function NoteCard({ note, onComplete, onDelete, onRestore, onEdit, onGoToPF, isArchived, formatReminder }) {
  const color = NOTE_COLORS[safeColorIdx(note.color)];
  const reminder = formatReminder?.(note.reminder);

  return (
    <motion.div
      layout
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -10 }}
      className={`${color.bg} ${color.border} ${color.text} border-2 rounded-lg p-3 shadow-md relative group`}
    >
      {/* PF Link Badge */}
      {note.pf_number && (
        <button
          onClick={() => onGoToPF?.(note.pf_number)}
          className="mb-2 inline-flex items-center gap-1 px-2 py-1 bg-white/60 hover:bg-white rounded text-xs font-medium text-blue-700 hover:text-blue-900 transition-colors"
          title={`Go to PF ${note.pf_number}`}
        >
          <FolderOpen className="h-3 w-3" />
          {note.pf_number}
          <ExternalLink className="h-2.5 w-2.5" />
        </button>
      )}

      {/* Content */}
      <p className="text-sm whitespace-pre-wrap pr-6">{note.content}</p>

      {/* Reminder Badge */}
      {reminder && (
        <div
          className={`mt-2 text-xs flex items-center gap-1 ${
            reminder.isOverdue ? "text-red-600" : "text-slate-600"
          }`}
        >
          <Bell className="h-3 w-3" />
          {reminder.text}
          {reminder.isOverdue && <span className="font-medium">(Overdue)</span>}
        </div>
      )}

      {/* Actions */}
      <div className="absolute top-2 right-2 flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
        {isArchived ? (
          <>
            <button
              onClick={onRestore}
              className="p-1 hover:bg-white/50 rounded"
              title="Restore"
            >
              <Archive className="h-3.5 w-3.5" />
            </button>
            <button
              onClick={onDelete}
              className="p-1 hover:bg-red-200 rounded text-red-600"
              title="Delete"
            >
              <Trash2 className="h-3.5 w-3.5" />
            </button>
          </>
        ) : (
          <>
            <button
              onClick={onEdit}
              className="p-1 hover:bg-white/50 rounded"
              title="Edit"
            >
              <Edit2 className="h-3.5 w-3.5" />
            </button>
            <button
              onClick={onComplete}
              className="p-1 hover:bg-green-200 rounded text-green-600"
              title="Complete"
            >
              <Check className="h-3.5 w-3.5" />
            </button>
            <button
              onClick={onDelete}
              className="p-1 hover:bg-red-200 rounded text-red-600"
              title="Delete"
            >
              <Trash2 className="h-3.5 w-3.5" />
            </button>
          </>
        )}
      </div>

      {/* Created date */}
      <p className="text-[10px] text-slate-400 mt-2">
        {new Date(note.created_at).toLocaleDateString()}
      </p>
    </motion.div>
  );
}
